import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import DataTable from "react-data-table-component";

const API_URL = "api/v1/talent-scan-parsing-logs/";

function getAuthToken() {
  return (
    (typeof window !== "undefined" &&
      window.localStorage.getItem("authToken")) ||
    ""
  );
}

const STATUS_STYLES = {
  success: { bg: "#ecfdf3", fg: "#027a48", dot: "#12b76a" },
  completed: { bg: "#ecfdf3", fg: "#027a48", dot: "#12b76a" },
  failed: { bg: "#fef3f2", fg: "#b42318", dot: "#f04438" },
  error: { bg: "#fef3f2", fg: "#b42318", dot: "#f04438" },
  pending: { bg: "#fffaeb", fg: "#b54708", dot: "#f79009" },
  processing: { bg: "#eff8ff", fg: "#175cd3", dot: "#2e90fa" },
};

function pick(row, keys) {
  for (const k of keys) {
    if (row[k] !== undefined && row[k] !== null && row[k] !== "") return row[k];
  }
  return null;
}

const CANDIDATE_KEYS = ["candidate_name", "candidateName", "candidate", "name"];
const FILE_KEYS = ["file_name", "fileName", "file", "resume"];
const STATUS_KEYS = ["status", "parsing_status"];
const ERROR_KEYS = ["error_message", "error"];
const CREATED_KEYS = ["created_at", "createdAt"];

function StatusBadge({ value }) {
  if (!value) return <span>—</span>;
  const key = String(value).toLowerCase();
  const style = STATUS_STYLES[key] || {
    bg: "#f3f4f6",
    fg: "#374151",
    dot: "#9ca3af",
  };

  return (
    <span style={{ ...styles.badge, background: style.bg, color: style.fg }}>
      <span style={{ ...styles.badgeDot, background: style.dot }} />
      {value}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  return isNaN(d) ? value : d.toLocaleString();
}

const columns = [
  { name: "ID", selector: (row) => row.id, width: "70px" },
  {
    name: "Candidate",
    cell: (row) => pick(row, CANDIDATE_KEYS) || "—",
  },
  {
    name: "File",
    cell: (row) => pick(row, FILE_KEYS) || "—",
  },
  {
    name: "Status",
    cell: (row) => <StatusBadge value={pick(row, STATUS_KEYS)} />,
  },
  {
    name: "Error",
    cell: (row) => pick(row, ERROR_KEYS) || "—",
  },
  {
    name: "Created",
    cell: (row) => formatDate(pick(row, CREATED_KEYS)),
  },
];

export default function TalentScanParsingLogsPage() {
  const [rows, setRows] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const fetchLogs = useCallback(async (pageNum, size) => {
    setLoading(true);
    setError(null);

    try {
      const token = getAuthToken();

      const res = await axios.get(API_URL, {
        params: { page: pageNum, page_size: size },
      });

      const data = res.data;
      const results = data.results || data.data || data || [];

      setRows(results);
      setCount(data.count || results.length);
    } catch (err) {
      setError("Failed to load data");
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLogs(page, pageSize);
  }, [page, pageSize, fetchLogs]);

  const filteredRows = rows.filter((row) =>
    search
      ? JSON.stringify(row).toLowerCase().includes(search.toLowerCase())
      : true
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <h2>Talent Scan Parsing Logs</h2>

        <div style={styles.actions}>
          <input
            placeholder="Search..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.input}
          />

          <button onClick={() => fetchLogs(page, pageSize)} style={styles.button}>
            Refresh
          </button>
        </div>
      </div>

      {error && <div style={styles.error}>{error}</div>}

      <div style={styles.card}>
        <DataTable
          columns={columns}
          data={filteredRows}
          progressPending={loading}
          pagination
          paginationServer
          paginationTotalRows={count}
          onChangePage={(p) => setPage(p)}
          onChangeRowsPerPage={(r) => setPageSize(r)}
          striped
        />
      </div>
    </div>
  );
}

const styles = {
  page: { padding: 24, fontFamily: "sans-serif" },
  header: { display: "flex", justifyContent: "space-between", marginBottom: 16 },
  actions: { display: "flex", gap: 10 },
  input: { padding: 6, borderRadius: 6, border: "1px solid #ccc" },
  button: { padding: "6px 12px", cursor: "pointer" },
  error: { color: "red", marginBottom: 10 },
  card: { border: "1px solid #eee", borderRadius: 8, overflow: "hidden" },
  badge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 5,
    padding: "3px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 600,
  },
  badgeDot: { width: 6, height: 6, borderRadius: "50%" },
};